import { Component, forwardRef, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FormValidation } from '@core/services/form-validation';

@Component({
  selector: 'app-date-picker',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './date-picker.html',
  styleUrls: ['./date-picker.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePicker),
      multi: true,
    },
  ],
})
export class DatePicker implements ControlValueAccessor, OnInit {
  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() formControlName: string = '';
  @Input() control: AbstractControl | null = new FormControl();
  @Input() date: 'date' | 'time' | 'datetime-local' = 'datetime-local'; // default full date-time
  @Input() size: 'small' | 'medium' | 'large' = 'medium';
  value: string | null = null;
  disabled = false;

  minDateTime: string = ''; // to store the current date/time (for disabling past times)

  constructor(public formValidation: FormValidation) {}

  ngOnInit(): void {
    this.setMinDateTime();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['date']) {
      this.setMinDateTime();
    }
  }

  // Sets the minimum datetime (current date and time)
  setMinDateTime(): void {
    const now = new Date();

    // Format based on input type
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');

    if (this.date === 'date') {
      this.minDateTime = `${year}-${month}-${day}`;
    } else if (this.date === 'time') {
      this.minDateTime = `${hours}:${minutes}`;
    } else {
      // datetime-local
      this.minDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;
    }
  }

  // ControlValueAccessor
  writeValue(value: any): void {
    this.value = value || null;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Handlers
  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  onInput(event: Event): void {
    // Keep the min up-to-date at the time user interacts.
    this.setMinDateTime();

    const input = event.target as HTMLInputElement;
    this.value = input.value;

    // Guard: if user types a past value manually, clear it so validation can trigger.
    if (this.value && this.minDateTime && this.value < this.minDateTime) {
      this.value = null;
      input.value = '';
    }

    this.onChange(this.value);
    this.onTouched();
  }

  // Validation Helpers
  get errorMessage(): string | null {
    if (this.control?.touched && this.control?.invalid) {
      return this.formValidation.getValidationMessage(this.control, this.formControlName);
    }
    return null;
  }

  get isInvalid(): boolean {
    return (this.control?.invalid && this.control?.touched) || false;
  }

  get required(): boolean {
    if (!this.control || !this.control.validator) return false;
    const validator = this.control.validator({} as AbstractControl);
    return !!validator?.['required'];
  }
}
